import React from 'react'
import { Row, Col, Table } from 'react-bootstrap'
import { BiRefresh } from 'react-icons/bi'

import { getRowLabels, getSulcusReminder, formatDiopters, formatAxis, MIN_SAFE_POWER, MAX_SAFE_POWER,
         TORIC_POWER_MIN, TORIC_POWER_MAX } from './IOLExchangeCalculation'
import LoadingDots from '../common/LoadingDots'

// section: 'all' (default, large layout — recommendation + ladder stacked)
// | 'recommendation' | 'ladder' (compact layout — each in its own panel).
//
// stale: the form was edited after this result was calculated. The result is
// dimmed and the recommendation panel offers onRecalculate.
//
// `ladder` is the adapted engine result (services/IOLExchange.js). The
// calculation runs on the server, so this component only renders — it never
// computes a refraction itself.

// Where to place the toric lens: the suggested axis (Calculadora!F13), unless the closest
// cylinder is 0. A 0 cylinder is a non-toric lens, which has no axis (the engine gives
// that row none), and the workbook writes "Sin eje" when no cylinder is needed at all.
function getPlacementAxis(toric){
    if(!toric || toric.suggestedAxis === null || toric.suggestedAxis === undefined) return null;
    let selected = toric.options.find((option) => option.selected);
    if(selected && selected.cylinder === 0) return null;
    return toric.suggestedAxis;
}

export default function Result({showResult, changeModel, targetMRSE, newPosition, section,
                                ladder, calculating, error, stale, onRecalculate}) {

    const showRecommendation = (section !== 'ladder');
    const showLadder = (section !== 'recommendation');

    if(!showResult){
        return (
            <div className='iolx-result-empty vCenter'>
                <span>
                    {(section === 'ladder')?
                        <>The power ladder appears here after you press <b>Calculate exchange</b>.</>
                    :(section === 'recommendation')?
                        <>The recommended power appears here after you press <b>Calculate exchange</b>.</>
                    :
                        <>Complete the form and press <b>Calculate exchange</b>.<br></br>
                        The recommended power and the power ladder appear here.</>
                    }
                </span>
            </div>
        )
    }

    if(calculating){
        return (
            <div className='iolx-result-empty vCenter'>
                <LoadingDots/>
            </div>
        )
    }

    if(error){
        return (
            <div className='iolx-result-empty vCenter'>
                <span className='iolx-warning'>{error}</span>
            </div>
        )
    }

    if(!ladder) return null;

    const noRecommendation = (ladder.noEligibleCandidate || ladder.recommendedPower === null);
    const sulcusReminder = getSulcusReminder(ladder);
    const placeAxis = getPlacementAxis(ladder.toric);

    return (
        <div className={'iolx-result' + ((stale)? ' iolx-stale' : '')}>

            {(stale && showRecommendation)?
                <div className='iolx-stale-banner'>
                    <span>Inputs changed since this result.</span>
                    <button type='button' className='iolx-recalculate-btn' onClick={onRecalculate}>
                        <BiRefresh/> Recalculate
                    </button>
                </div>
            : null}

            {/* RECOMMENDATION */}
            {(showRecommendation)?
            <Row className='iolx-recommendation'>
                <Col xs={12} className='textCenter iolx-recommendation-item'>
                    <span className='iolx-result-caption'>Recommended power</span>
                    <span className='iolx-result-value iolx-result-value-big'>
                        {(noRecommendation)? "—" : ladder.recommendedPower.toFixed(1) + " D"}
                    </span>
                </Col>
                <Col xs={12} className='textCenter iolx-recommendation-item'>
                    <span className='iolx-result-caption'>
                        {(noRecommendation)? "Actual refraction (MRSE)" : "Predicted MRSE at that power"}
                    </span>
                    <span className='iolx-result-value iolx-result-value-big'>
                        {(noRecommendation)? formatDiopters(ladder.actualRefraction)
                                           : formatDiopters(ladder.recommendedMRSE)} D
                    </span>
                </Col>
                <Col xs={12} className='textCenter'>
                    <span className='iolx-note'>
                        Target MRSE {(targetMRSE !== "")? targetMRSE : "0.00"} D ·&nbsp;
                        {newPosition} · {(changeModel)? "IOL model changes" : "same IOL model"}
                    </span>
                </Col>
                {/* The "Actual" row: current MRSE after the A-constant shift,
                    applied once at the implanted power (SRS 1.5, [F1]). */}
                <Col xs={12} className='textCenter'>
                    <span className='iolx-note'>
                        Actual refraction at {ladder.actualPower.toFixed(1)} D:&nbsp;
                        {formatDiopters(ladder.actualRefraction)} D
                        {(changeModel && ladder.aConstantShift !== 0)?
                            <> · A-constant shift {formatDiopters(ladder.aConstantShift)} D</>
                        : null}
                    </span>
                </Col>
            </Row>
            : null}

            {(showRecommendation && noRecommendation)?
                <Row>
                    <span className='iolx-warning'>
                        No candidate reaches the target without a hyperopic result.
                        No recommendation is given — review the ladder or adjust the target.
                    </span>
                </Row>
            : null}

            {/* SRS 1.7: whenever sulcus is selected the UI shows a reminder, as
                No-Biometry IOLX!F18 does - also when there is no bag candidate, or
                no candidate at all. When both ladders recommend, it carries the bag
                power too, so the surgeon sees the size of the adjustment; that is
                reference information, never a fallback. A warning rather than a
                note: SRS 1.7 requires it, so it must stand out, not read as an aside. */}
            {(showRecommendation && sulcusReminder)?
                <Row>
                    <span className='iolx-warning'>{sulcusReminder}</span>
                </Row>
            : null}

            {(showRecommendation && ladder.extrapolated)?
                <Row>
                    <span className='iolx-warning'>
                        Prediction outside {MIN_SAFE_POWER}–{MAX_SAFE_POWER} D — extrapolated.
                    </span>
                </Row>
            : null}

            {(showRecommendation && ladder.aConstantExtrapolated)?
                <Row>
                    <span className='iolx-warning'>
                        A-constant adjustment extrapolated beyond the published 0–35 D range.
                    </span>
                </Row>
            : null}

            {(showRecommendation && ladder.toricOutsideTable)?
                <Row>
                    <span className='iolx-warning'>
                        No toric result: the recommended power is outside the
                        {" " + TORIC_POWER_MIN.toFixed(1)}–{TORIC_POWER_MAX.toFixed(1)} D the toric tables cover.
                    </span>
                </Row>
            : null}

            {(showRecommendation && ladder.toricOutsideOptions)?
                <Row>
                    <span className='iolx-warning'>
                        The cylinder needed is outside the available IOL cylinders; the closest one is marked.
                    </span>
                </Row>
            : null}

            {/* POWER LADDER */}
            {(showLadder)?
            <Row className='iolx-ladder-row'>
                <span className='iolx-result-caption'>Power ladder</span>
                {(ladder.rows.length === 0)?
                    <span className='iolx-note'>
                        No ladder is shown when no candidate reaches the target.
                    </span>
                :
                <Table className='iolx-ladder-table' hover>
                    <thead>
                        <tr>
                            <th>{(changeModel)? "New IOL power (D)" : "IOL power (D)"}</th>
                            <th>Bag-equivalent (D)</th>
                            <th>Predicted MRSE (D)</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ladder.rows.map((row) => {
                            let labels = getRowLabels(row, changeModel);
                            return (
                                <tr key={row.power}
                                className={(row.isRecommended)? 'iolx-row-rec'
                                         : (row.isActual)? 'iolx-row-actual' : null}>
                                    <td>{row.power.toFixed(1)}</td>
                                    <td>{row.bagEquivalentPower.toFixed(1)}</td>
                                    <td>{formatDiopters(row.predictedMRSE)}</td>
                                    <td className='iolx-ladder-labels'>
                                        {labels.map((label) =>
                                            <span key={label} className='iolx-ladder-label'>{label}</span>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
                }
            </Row>
            : null}

            {/* TORIC — Dr. Buonsanti's workbook, Calculadora!F13 and A27:G42, at the
                recommended power. Every available cylinder with the cylinder and axis
                it would leave; the one closest to the requirement is marked. Worded as
                Dr. Buonsanti asked (31 Oct): "Suggested IOL 19.0 D at 10.0°", and
                "Place IOL at 10.0°" in large type. */}
            {(showLadder && ladder.toric)?
            <Row className='iolx-ladder-row iolx-toric-row'>
                <span className='iolx-result-caption'>
                    Suggested IOL {ladder.recommendedPower.toFixed(1)} D
                    {(placeAxis !== null)? " at " + formatAxis(placeAxis) : ""}
                </span>
                {(placeAxis !== null)?
                    <span className='iolx-toric-place'>
                        Place IOL at {formatAxis(placeAxis)}
                    </span>
                : null}
                <Table className='iolx-ladder-table iolx-toric-table' hover>
                    <thead>
                        <tr>
                            <th>IOL cylinder (D)</th>
                            <th>Residual cylinder (D)</th>
                            <th>Refractive axis</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ladder.toric.options.map((option) =>
                            <tr key={option.cylinder} className={(option.selected)? 'iolx-row-rec' : null}>
                                <td>{option.cylinder.toFixed(2)}</td>
                                <td>{formatDiopters(option.residualCylinder)}</td>
                                <td>{formatAxis(option.residualAxis)}</td>
                                <td className='iolx-ladder-labels'>
                                    {(option.selected)? <span className='iolx-ladder-label'>Rec</span> : null}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Row>
            : null}
        </div>
    )
}
